import { useEffect, useState } from "react";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function valorMonetario(valor: unknown) {
  return `R$ ${Number(valor ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function dataHoraTela(valor: unknown) {
  return valor ? new Date(String(valor)).toLocaleString("pt-BR") : "—";
}

function textoEnderecos(valor: unknown) {
  return `${Number(valor ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pontos de armazenamento`;
}

function QuadroFechamento({ fechamento, competencia }: { fechamento: any; competencia: string }) {
  const resumo = fechamento?.resumo;
  const configuracao = fechamento?.configuracao;
  if (!resumo || !configuracao) return null;

  const cartoes = [
    { titulo: "PEDIDOS", valor: resumo.totalPedidosExpedidos, detalhe: `${resumo.totalPedidos} pedido(s)`, classe: "bg-[#1F5D8F] text-white" },
    { titulo: "RESOLVE", valor: resumo.totalImpactoResolve, detalhe: `${resumo.totalSolicitacoesResolve} solicitação(ões)`, classe: "bg-[#4A5D68] text-white" },
    { titulo: "PONTOS DE ARMAZENAMENTO", valor: resumo.totalArmazenagem, detalhe: textoEnderecos(resumo.totalEnderecos), classe: "bg-[#C9F1E3] text-[#173E35]" },
    { titulo: "TOTAL GERAL", valor: resumo.totalGeral, detalhe: `${resumo.totalProdutosArmazenagemNaoCobrada} produto(s) com armazenagem não cobrada`, classe: "bg-[#163A56] text-white" },
  ];
  const linhas = [
    ["Pedidos", `${resumo.totalPedidos} pedido(s)`, configuracao.valorPacoteExpedido, resumo.totalPedidosExpedidos],
    ["Resolve", `${resumo.totalSolicitacoesResolve} solicitação(ões)`, null, resumo.totalImpactoResolve],
    ["Pontos de armazenamento", textoEnderecos(resumo.totalEnderecos), configuracao.valorM2Armazenado, resumo.totalArmazenagem],
  ];

  return (
    <section className="overflow-x-auto bg-white">
      <div className="min-w-[860px]">
        <div className="bg-[#174466] px-8 py-5 text-center text-[27px] font-bold uppercase tracking-wide text-white">FECHAMENTO BOX — B2C + ARMAZENAGEM</div>
        <div className="border-x border-b border-slate-200 px-6 py-2 text-center text-sm font-semibold text-slate-600">Emitente: {fechamento.emitente?.emitenteNome || fechamento.emitenteNome || "—"} <span className="mx-2">|</span> Competência: {competencia}</div>
        <div className="mt-8 grid grid-cols-4 border-x border-white">
          {cartoes.map((cartao) => (
            <div key={cartao.titulo} className={`min-h-32 border-r border-white px-4 py-5 text-center ${cartao.classe}`}>
              <p className="text-sm font-bold">{cartao.titulo}</p>
              <p className="mt-2 text-2xl font-bold">{valorMonetario(cartao.valor)}</p>
              <p className="mt-1 text-sm font-medium">{cartao.detalhe}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 overflow-hidden border border-slate-200">
          <div className="bg-[#174466] px-4 py-2 text-center text-sm font-bold uppercase text-white">COMPOSIÇÃO DO FECHAMENTO</div>
          <table className="w-full text-sm">
            <thead className="bg-[#174466] text-white">
              <tr><th className="border-r border-white/40 px-4 py-3 text-left">Categoria</th><th className="border-r border-white/40 px-4 py-3 text-right">Quantidade</th><th className="border-r border-white/40 px-4 py-3 text-right">Valor unitário</th><th className="px-4 py-3 text-right">Impacto no fechamento</th></tr>
            </thead>
            <tbody>
              {linhas.map(([categoria, quantidade, unitario, impacto]) => (
                <tr key={String(categoria)} className={categoria === "Pontos de armazenamento" ? "bg-[#EAF8F2] font-semibold text-[#17614D]" : "border-t border-slate-200"}>
                  <td className="border-r border-slate-200 px-4 py-3">{categoria}</td>
                  <td className="border-r border-slate-200 px-4 py-3 text-right">{quantidade}</td>
                  <td className="border-r border-slate-200 px-4 py-3 text-right">{unitario === null ? "-" : valorMonetario(unitario)}</td>
                  <td className="px-4 py-3 text-right">{valorMonetario(impacto)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#3A5566] font-bold text-white">
              <tr><td className="px-4 py-3" colSpan={3}>TOTAL GERAL</td><td className="px-4 py-3 text-right">{valorMonetario(resumo.totalGeral)}</td></tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>
  );
}

export default function FaturasOperacao() {
  const { user } = useGoogleAuth();
  const [emitenteId, setEmitenteId] = useState("");
  const [competenciaFechamento, setCompetenciaFechamento] = useState("");
  const { data: emitentes = [], isLoading: carregandoEmitentes } = trpc.faturasOperacao.listEmitentes.useQuery();
  const emitentesAutorizados = emitentes as any[];

  useEffect(() => {
    if (!emitenteId && emitentesAutorizados.length === 1) setEmitenteId(String(emitentesAutorizados[0].id));
  }, [emitenteId, emitentesAutorizados]);

  const { data: dadosProjecao, isLoading: carregandoProjecao } = trpc.faturasOperacao.consultar.useQuery(
    { emitenteId: Number(emitenteId) },
    { enabled: !!emitenteId }
  );
  const { data: fechamentoSelecionado, isLoading: carregandoFechamento } = trpc.faturasOperacao.consultarFechamento.useQuery(
    { emitenteId: Number(emitenteId), competencia: competenciaFechamento },
    { enabled: !!emitenteId && !!competenciaFechamento }
  );
  const baixarExcel = trpc.faturasOperacao.baixarExcel.useMutation({
    onSuccess: (arquivo) => {
      const link = document.createElement("a");
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${arquivo.arquivoBase64}`;
      link.download = arquivo.nomeArquivo;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const selecionado = emitentesAutorizados.find((emitente) => String(emitente.id) === emitenteId);
  const mostrarSeletor = user?.role === "admin" || emitentesAutorizados.length > 1;

  return (
    <div className="min-h-full bg-slate-100 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Box Warehouse</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">Faturas de Operação</h1>
            <p className="mt-1 text-sm text-slate-600">Projeção atual e fechamento operacional por competência.</p>
          </div>
          {mostrarSeletor && <div className="w-full md:w-80"><Select value={emitenteId} onValueChange={setEmitenteId}><SelectTrigger className="bg-white"><SelectValue placeholder="Selecione o emitente" /></SelectTrigger><SelectContent>{emitentesAutorizados.map((emitente) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.emitenteNome}</SelectItem>)}</SelectContent></Select></div>}
        </header>

        {carregandoEmitentes ? <Card><CardContent className="p-6 text-sm text-slate-600">Carregando emitentes autorizados...</CardContent></Card> : emitentesAutorizados.length === 0 ? <Card><CardContent className="p-6 text-sm text-slate-600">Nenhum emitente autorizado para esta conta.</CardContent></Card> : !emitenteId ? <Card><CardContent className="p-6 text-sm text-slate-600">Selecione um emitente para consultar as faturas de operação.</CardContent></Card> : <div className="space-y-8">
          <section className="space-y-3"><h2 className="text-xl font-bold text-slate-900">Projeção atual de gastos</h2>{carregandoProjecao ? <Card><CardContent className="p-6 text-sm text-slate-600">Calculando projeção atual...</CardContent></Card> : dadosProjecao?.projecao ? <QuadroFechamento fechamento={dadosProjecao.projecao} competencia={dadosProjecao.competencia} /> : <Card><CardHeader><CardTitle>Nenhuma planilha de saldo disponível</CardTitle><CardDescription>Não existe planilha de saldo salva para este emitente.</CardDescription></CardHeader></Card>}</section>
          <section className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div><h2 className="text-xl font-bold text-slate-900">Fechamento anterior</h2><p className="text-sm text-slate-600">Selecione o mês e o ano para consultar o último fechamento criado naquela competência.</p></div>
              <Input className="w-full bg-white md:w-48" type="month" value={competenciaFechamento} onChange={(evento) => setCompetenciaFechamento(evento.target.value)} aria-label="Mês e ano do fechamento" />
            </div>
            {!competenciaFechamento ? <Card><CardContent className="p-6 text-sm text-slate-600">Selecione uma competência para consultar o fechamento.</CardContent></Card> : carregandoFechamento ? <Card><CardContent className="p-6 text-sm text-slate-600">Carregando fechamento...</CardContent></Card> : !fechamentoSelecionado ? <Card><CardContent className="p-6 text-sm text-slate-600">Nenhum fechamento salvo para a competência selecionada.</CardContent></Card> : <div className="space-y-3"><div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"><p className="text-sm text-slate-600">Criado em {dataHoraTela(fechamentoSelecionado.criadoEm)}.</p><Button onClick={() => baixarExcel.mutate({ fechamentoId: fechamentoSelecionado.id, emitenteId: Number(emitenteId), competencia: competenciaFechamento })} disabled={baixarExcel.isPending}><Download className="mr-2 h-4 w-4" />{baixarExcel.isPending ? "Preparando download..." : "Baixar Excel detalhado"}</Button></div><QuadroFechamento fechamento={{ ...fechamentoSelecionado, emitenteNome: selecionado?.emitenteNome }} competencia={competenciaFechamento} /></div>}
          </section>
        </div>}
        <p className="flex items-center gap-2 text-xs text-slate-500"><FileSpreadsheet className="h-4 w-4" />A projeção usa a competência atual e a última planilha de saldo salva para o emitente. O fechamento anterior usa exclusivamente a competência selecionada.</p>
      </div>
    </div>
  );
}
